import React, { useEffect, useState, useMemo } from "react";
import { axios } from "../../lib/constants/api";
import { useParams } from "react-router-dom";
import "./gallery.style.scss";
import { Loader } from "../../lib/ui/components";
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";

export const GalleryComponent: React.FC = () => {
  const apiUrls = useMemo(() => ({
    archiveImage: (productId) => `/upload-media-oc/product-images/${productId}`,
    upload: "/upload-media/media",
    updateImages: (productId) =>
      `/upload-media/product-images/${productId}?override=true`,
    fetchImages: (productId) => `/upload-media/product-images/${productId}`,
    fetchProductInfo: (productId) => `/products/panel/${productId}`,
    deleteProductImage: (productId) =>
      `/upload-media/product-images/${productId}?override=true`,
  }), []);

  const { productId } = useParams<{ productId: string }>();
  const [productData, setProductData] = useState(null);
  const [productImages, setProductImages] = useState([]);
  const [isLoadingVisible, setIsLoadingVisible] = useState(true);
  const [uploadingImages, setUploadingImages] = useState([]);
  const [uploadingImagesPreview, setUploadingImagesPreview] = useState([]);
  const [uploadingIndex, setUploadingIndex] = useState<number>(0);
  const [isUploading, setIsUploading] = useState(false);
  const [deletingIndexes, setDeletingIndexes] = useState([]);
  const [isArchiving, setIsArchiving] = useState(false);
  const [selectedImages, setSelectedImages] = useState(new Set());
  const [isBulkDeleting, setIsBulkDeleting] = useState(false);
  const [isDraggingOver, setIsDraggingOver] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [pendingChanges, setPendingChanges] = useState([]);
  const [isSaving, setIsSaving] = useState(false);

  const isQueueEmpty = uploadingImages.length === 0;

  useEffect(() => {
    let isMounted = true;
    
    const fetchImages = async () => {
      try {
        const response = await axios.get(apiUrls.fetchImages(productId));
        if (isMounted) {
          setProductImages(response.data.images);
          setIsLoadingVisible(false);
        }
      } catch (error) {
        if (isMounted) {
          console.error('Error fetching images:', error);
          setIsLoadingVisible(false);
        }
      }
    };

    if (productId) {
      fetchImages();
    }
    
    return () => {
      isMounted = false;
    };
  }, [productId, apiUrls]);

  useEffect(() => {
    let isMounted = true;
    
    const fetchData = async () => {
      try {
        const response = await axios.get(apiUrls.fetchProductInfo(productId));
        if (isMounted) {
          setProductData(response.data);
        }
      } catch (error) {
        if (isMounted) {
          console.error('Error fetching product data:', error);
        }
      }
    };

    if (productId) {
      fetchData();
    }
    
    return () => {
      isMounted = false;
    };
  }, [productId, apiUrls]);

  useEffect(() => {
    // Add the class when the component mounts
    document.body.classList.add("gallery");
    
    // Cleanup function to remove the class when component unmounts
    return () => {
      document.body.classList.remove("gallery");
    };
  }, []);

  const handleUploadImages = async () => {
    setIsUploading(true);
    const uploadedImages = [];

    try {
      // Create FormData with all files at once
      const formData = new FormData();
      uploadingImages.forEach((file, index) => {
        formData.append(`files`, file);
      });

      // Upload all images in a single request
      const response = await axios.post(apiUrls.upload, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      const responseData = response.data;
      
      // Handle response - it should be an array of uploaded images
      if (Array.isArray(responseData)) {
        responseData.forEach((imageData) => {
          uploadedImages.push({
            id: imageData.id,
            src: imageData.source_url
          });
        });
      } else {
        // Fallback for single image response
        uploadedImages.push({
          id: responseData.id,
          src: responseData.source_url
        });
      }

      // Get current images and add new ones
      const currentImages = productImages || [];
      const updatedImages = [...currentImages, ...uploadedImages];

      // Update the product with all images using id instead of src
      const payload = updatedImages.map((image) => ({ 
        id: image.id || image.src // fallback to src if id doesn't exist
      }));
      await axios.put(apiUrls.updateImages(productId), payload);

      // Update local state
      setProductImages(updatedImages);
      setUploadingImages([]);
      setUploadingImagesPreview([]);
    } catch (error) {
      console.error("Error uploading images:", error);
      // If bulk upload fails, fall back to individual uploads
      await handleIndividualUploads();
    } finally {
      setIsUploading(false);
    }
  };

  const handleIndividualUploads = async () => {
    let currentUploadingIndex = -1;
    const uploadedImages = [];

    for (const uploadingImage of uploadingImages) {
      currentUploadingIndex++;
      setUploadingIndex(currentUploadingIndex);

      const formData = new FormData();
      formData.append("file", uploadingImage);

      try {
        const response = await axios.post(apiUrls.upload, formData);
        const responseData = await response.data;
        // Store both id and src for the uploaded image
        uploadedImages.push({
          id: responseData.id,
          src: responseData.source_url
        });
      } catch (error) {
        console.error("Error uploading image:", error);
        return; // Stop uploading if one fails
      }
    }

    try {
      // Get current images and add new ones
      const currentImages = productImages || [];
      const updatedImages = [...currentImages, ...uploadedImages];

      // Update the product with all images using id instead of src
      const payload = updatedImages.map((image) => ({ 
        id: image.id || image.src // fallback to src if id doesn't exist
      }));
      await axios.put(apiUrls.updateImages(productId), payload);

      // Update local state
      setProductImages(updatedImages);
      setUploadingImages([]);
      setUploadingImagesPreview([]);
    } catch (error) {
      console.error("Error updating product:", error);
    }
  };

  const handleRemovePendingImage = (index) => {
    const newUploadingImages = uploadingImages.filter((_, i) => i !== index);
    const newUploadingImagesPreview = uploadingImagesPreview.filter(
      (_, i) => i !== index
    );
    setUploadingImages(newUploadingImages);
    setUploadingImagesPreview(newUploadingImagesPreview);
  };

  const handleDragEnd = async (result) => {
    setIsDraggingOver(false);
    if (!result.destination) return;

    const items = Array.from(productImages);
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);

    if (isEditMode) {
      // In edit mode, just update local state and track changes
      setProductImages(items);
      setPendingChanges((prev) => [...prev, { type: "reorder", data: items }]);
    } else {
      // In view mode, save immediately
      setProductImages(items);
      try {
        const payload = items.map((image) => ({ 
          id: image.id || image.src // fallback to src if id doesn't exist
        }));
        await axios.put(apiUrls.updateImages(productId), payload);
      } catch (error) {
        console.error("Error updating image order:", error);
        // Revert on error
        const fetchResponse = await axios.get(apiUrls.fetchImages(productId));
        setProductImages(fetchResponse.data.images);
      }
    }
  };

  const handleDragStart = () => {
    setIsDraggingOver(true);
  };

  const handleDragUpdate = (update) => {
    if (!update.destination) return;

    // Auto-scroll when dragging near edges
    const container = document.querySelector(".images-grid");
    if (container) {
      const rect = container.getBoundingClientRect();
      const scrollSpeed = 15;

      if (update.clientX < rect.left + 100) {
        container.scrollLeft -= scrollSpeed;
      } else if (update.clientX > rect.right - 100) {
        container.scrollLeft += scrollSpeed;
      }
    }
  };

  const handleImageSelect = (index) => {
    const newSelectedImages = new Set(selectedImages);
    if (newSelectedImages.has(index)) {
      newSelectedImages.delete(index);
    } else {
      newSelectedImages.add(index);
    }
    setSelectedImages(newSelectedImages);
  };

  const handleBulkDelete = async () => {
    if (selectedImages.size === 0) return;

    if (isEditMode) {
      // In edit mode, just update local state and track changes
      const selectedIndexes = Array.from(selectedImages).sort(
        (a: number, b: number) => b - a
      ) as number[];
      let newProductImages = [...productImages];

      // Remove selected images from the end to avoid index shifting issues
      for (const index of selectedIndexes) {
        newProductImages.splice(index, 1);
      }

      setProductImages(newProductImages);
      setSelectedImages(new Set());
      setPendingChanges((prev) => [
        ...prev,
        {
          type: "bulkDelete",
          data: newProductImages,
          deletedIndexes: selectedIndexes,
        },
      ]);
    } else {
      // In view mode, save immediately
      setIsBulkDeleting(true);
      const selectedIndexes = Array.from(selectedImages).sort(
        (a: number, b: number) => b - a
      ) as number[];
      let newProductImages = [...productImages];

      // Remove selected images from the end to avoid index shifting issues
      for (const index of selectedIndexes) {
        newProductImages.splice(index, 1);
      }

      try {
        const payload = newProductImages.map((image) => ({ 
          id: image.id || image.src // fallback to src if id doesn't exist
        }));
        await axios.put(apiUrls.deleteProductImage(productId), payload);
        setProductImages(newProductImages);
        setSelectedImages(new Set());
      } catch (error) {
        console.error("Error bulk deleting images:", error);
      } finally {
        setIsBulkDeleting(false);
      }
    }
  };

  const handleSelectAll = () => {
    if (selectedImages.size === productImages.length) {
      setSelectedImages(new Set());
    } else {
      setSelectedImages(new Set(productImages.map((_, index) => index)));
    }
  };

  const handleSaveChanges = async () => {
    if (pendingChanges.length === 0) return;

    setIsSaving(true);
    try {
      // Get the final state from the last change
      const finalChange = pendingChanges[pendingChanges.length - 1];
      const finalImages = finalChange.data;

      const payload = finalImages.map((image) => ({ 
        id: image.id || image.src // fallback to src if id doesn't exist
      }));
      await axios.put(apiUrls.updateImages(productId), payload);

      // Clear pending changes and exit edit mode
      setPendingChanges([]);
      setIsEditMode(false);
      setSelectedImages(new Set());
    } catch (error) {
      console.error("Error saving changes:", error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancelEdit = () => {
    // Revert to original state
    setPendingChanges([]);
    setIsEditMode(false);
    setSelectedImages(new Set());
    // Refetch original data
    const fetchImages = async () => {
      try {
        const response = await axios.get(apiUrls.fetchImages(productId));
        setProductImages(response.data.images);
      } catch (error) {
        console.error("Error fetching images:", error);
      }
    };
    fetchImages();
  };

  const toggleEditMode = () => {
    if (isEditMode) {
      handleCancelEdit();
    } else {
      setIsEditMode(true);
      setSelectedImages(new Set());
      setPendingChanges([]);
    }
  };

  const handleRemoveUploadedImage = async (image, index) => {
    const newProductImages = productImages.filter((_, i) => i !== index);

    const clonedDeletingIndexes = [...deletingIndexes];
    setDeletingIndexes([...clonedDeletingIndexes, index]);

    try {
      const payload = newProductImages.map((image) => ({ 
        id: image.id || image.src // fallback to src if id doesn't exist
      }));

      await axios.put(apiUrls.deleteProductImage(productId), payload);
      setProductImages(newProductImages);

      const newDeletingIndexes = clonedDeletingIndexes.filter(
        (_, i) => i !== index
      );
      setDeletingIndexes(newDeletingIndexes);
    } catch (error) {
      console.error("Error updating product:", error);
    }
  };

  const handleArchiveImage = async (index, imageSrc) => {
    setIsArchiving(true);
    const newProductImages = productImages.filter((_, i) => i !== index);

    const clonedDeletingIndexes = [...deletingIndexes];
    setDeletingIndexes([...clonedDeletingIndexes, index]);

    try {
      const payload = newProductImages.map((image) => ({ 
        id: image.id || image.src // fallback to src if id doesn't exist
      }));
      await axios.put(apiUrls.archiveImage(productId), [{ src: imageSrc }]);
      await axios.put(apiUrls.deleteProductImage(productId), payload);
      setProductImages(newProductImages);

      const newDeletingIndexes = clonedDeletingIndexes.filter(
        (_, i) => i !== index
      );
      setDeletingIndexes(newDeletingIndexes);
      setIsArchiving(false);
    } catch (error) {
      console.error("Error updating product:", error);
    }
  };

  const handleImagesChange = async (event) => {
    const files = event.target.files;

    const newUploadingImages = [...uploadingImages];
    const newUploadingImagesPreview = [...uploadingImagesPreview];
    for (const file of files) {
      if (file) {
        newUploadingImages.push(file);
        newUploadingImagesPreview.push(URL.createObjectURL(file));
      }
    }
    setUploadingImages(newUploadingImages);
    setUploadingImagesPreview(newUploadingImagesPreview);
  };

  const shouldShowDeleteButton = (index) =>
    deletingIndexes.length ? deletingIndexes.includes(index) : !isUploading;

  const openProductPage = () => {
    if (!productData) return;
    window.open(productData.permalink, "_self");
  };

  return (
    <>
      <Loader
        visible={isLoadingVisible || isSaving || isBulkDeleting}
        text={
          isLoadingVisible
            ? "در حال بارگیری تصاویر..."
            : isSaving
            ? "در حال ذخیره تغییرات..."
            : isBulkDeleting
            ? "در حال حذف تصاویر..."
            : "در حال پردازش..."
        }
      />

      <div
        className={`gallery-container ${
          isSaving || isBulkDeleting ? "loading" : ""
        }`}
      >
        <button
          className="back-btn btn"
          disabled={!productData || !productData.permalink}
          onClick={() => openProductPage()}
        >
          بازگشت به صفحه محصول
        </button>
        {productData && (
          <h3 className="main-title">{`تصاویر محصول ${productData.name}`}</h3>
        )}
        <div className="images-container">
          <div className="flex">
            <h3 className="container-title">تصاویر دستگاه</h3>
            <div className="actions-container">
              <div className="upload">
                <input
                  disabled={isUploading || isEditMode}
                  onChange={handleImagesChange}
                  type="file"
                  multiple
                  id="images-upload"
                />
                <label htmlFor="images-upload">
                  <span
                    className={`btn ${
                      (isUploading || isEditMode) && "disabled"
                    }`}
                  >{`${
                    productImages.length > 0 ? "افزودن" : "بارگزاری"
                  } تصویر`}</span>
                </label>
              </div>
              <button
                className={`btn edit-mode-btn ${isEditMode ? "active" : ""}`}
                onClick={toggleEditMode}
                disabled={isUploading || isSaving}
              >
                {isEditMode ? "انصراف" : "ویرایش"}
              </button>
            </div>
          </div>

          {/* Bulk Actions - Only in Edit Mode */}
          {isEditMode && productImages.length > 0 && (
            <div className="bulk-actions">
              <div className="bulk-controls">
                <label className="select-all-checkbox">
                  <input
                    type="checkbox"
                    checked={selectedImages.size === productImages.length}
                    onChange={handleSelectAll}
                  />
                  انتخاب همه
                </label>
                {selectedImages.size > 0 && (
                  <button
                    className="btn bulk-delete-btn"
                    onClick={handleBulkDelete}
                    disabled={isBulkDeleting}
                  >
                    {isBulkDeleting
                      ? "در حال حذف..."
                      : `حذف ${selectedImages.size} تصویر انتخاب شده`}
                  </button>
                )}
              </div>
              <div className="edit-mode-instructions">
                💡 حالت ویرایش فعال است. برای ذخیره تغییرات روی دکمه "ذخیره"
                کلیک کنید
              </div>
            </div>
          )}

          {/* Save/Cancel Buttons - Only in Edit Mode */}
          {isEditMode && (
            <div className="edit-actions">
              <button
                className="btn save-btn"
                onClick={handleSaveChanges}
                disabled={isSaving || pendingChanges.length === 0}
              >
                {isSaving ? "در حال ذخیره..." : "ذخیره تغییرات"}
              </button>
              <button
                className="btn cancel-btn"
                onClick={handleCancelEdit}
                disabled={isSaving}
              >
                انصراف
              </button>
            </div>
          )}

          {/* Images Display */}
          {isEditMode ? (
            /* Edit Mode - Drag and Drop */
            <DragDropContext
              onDragEnd={handleDragEnd}
              onDragStart={handleDragStart}
              onDragUpdate={handleDragUpdate}
            >
              <Droppable droppableId="images" direction="horizontal">
                {(provided) => (
                  <div
                    className={`images-grid edit-mode ${
                      isDraggingOver ? "dragging-over" : ""
                    }`}
                    {...provided.droppableProps}
                    ref={provided.innerRef}
                  >
                    {productImages.map((image, index) => (
                      <Draggable
                        key={`${image.src}-${index}`}
                        draggableId={`${image.src}-${index}`}
                        index={index}
                      >
                        {(provided, snapshot) => (
                          <div
                            className={`image-container ${
                              snapshot.isDragging ? "dragging" : ""
                            }`}
                            ref={provided.innerRef}
                            {...provided.draggableProps}
                            {...provided.dragHandleProps}
                          >
                            {/* Main Image Label */}
                            {index === 0 && (
                              <div className="main-image-label">تصویر اصلی</div>
                            )}

                            {/* Selection Checkbox */}
                            <div className="selection-checkbox">
                              <input
                                type="checkbox"
                                checked={selectedImages.has(index)}
                                onChange={() => handleImageSelect(index)}
                              />
                            </div>

                            {/* Drag Handle */}
                            <div className="drag-handle">
                              <svg
                                width="16"
                                height="16"
                                viewBox="0 0 24 24"
                                fill="currentColor"
                              >
                                <path d="M3 18h18v-2H3v2zm0-5h18v-2H3v2zm0-7v2h18V6H3z" />
                              </svg>
                            </div>

                            <div className="cover-action">
                              {shouldShowDeleteButton(index) &&
                                !isArchiving && (
                                  <button
                                    className={
                                      deletingIndexes.includes(index)
                                        ? "texted"
                                        : ""
                                    }
                                    disabled={deletingIndexes.includes(index)}
                                    onClick={() =>
                                      handleRemoveUploadedImage(image, index)
                                    }
                                  >
                                    {deletingIndexes.includes(index)
                                      ? "در حال حذف"
                                      : `\u00D7`}
                                  </button>
                                )}
                              {shouldShowDeleteButton(index) && (
                                <button
                                  className={
                                    deletingIndexes.includes(index)
                                      ? "texted archive"
                                      : " archive"
                                  }
                                  disabled={deletingIndexes.includes(index)}
                                  onClick={() =>
                                    handleArchiveImage(index, image.src)
                                  }
                                >
                                  {deletingIndexes.includes(index) ? (
                                    "در حال انتقال"
                                  ) : (
                                    <svg
                                      xmlns="http://www.w3.org/2000/svg"
                                      width="16"
                                      height="16"
                                      fill="currentColor"
                                      className="bi bi-archive"
                                      viewBox="0 0 16 16"
                                    >
                                      <path d="M0 2a1 1 0 0 1 1-1h14a1 1 0 0 1 1 1v2a1 1 0 0 1-1 1v7.5a2.5 2.5 0 0 1-2.5 2.5h-9A2.5 2.5 0 0 1 1 12.5V5a1 1 0 0 1-1-1zm2 3v7.5A1.5 1.5 0 0 0 3.5 14h9a1.5 1.5 0 0 0 1.5-1.5V5zm13-3H1v2h14zM5 7.5a.5.5 0 0 1 .5-.5h5a.5.5 0 0 1 0 1h-5a.5.5 0 0 1-.5-.5" />
                                    </svg>
                                  )}
                                </button>
                              )}
                            </div>
                            <img
                              className={`image-item ${
                                index === 0 ? "main" : ""
                              }`}
                              src={image.src}
                              alt={image.src}
                              draggable={false}
                            />
                          </div>
                        )}
                      </Draggable>
                    ))}
                    {provided.placeholder}
                  </div>
                )}
              </Droppable>
            </DragDropContext>
          ) : (
            /* View Mode - Grid Layout */
            <div className="images-grid view-mode">
              {productImages.map((image, index) => (
                <div key={`${image.src}-${index}`} className="image-container">
                  {/* Main Image Label */}
                  {index === 0 && (
                    <div className="main-image-label">تصویر اصلی</div>
                  )}

                  <img
                    className={`image-item ${index === 0 ? "main" : ""}`}
                    src={image.src}
                    alt={image.src}
                  />
                </div>
              ))}
            </div>
          )}

          {/* Uploading Images Preview */}
          {uploadingImagesPreview.map((image, index) => (
            <div className="image-container preview">
              <div className="cover">
                {uploadingIndex > index
                  ? "آپلود شده"
                  : uploadingIndex === index
                  ? "در حال آپلود"
                  : "در صف آپلود"}
              </div>
              <div className="cover-action">
                {!isUploading && (
                  <button onClick={() => handleRemovePendingImage(index)}>
                    &times;
                  </button>
                )}
              </div>
              <img className="image-item" src={image} alt="text" />
            </div>
          ))}

          {!isQueueEmpty && (
            <button
              disabled={isUploading}
              onClick={() => handleUploadImages()}
              className="confirm-btn"
            >
              {isUploading ? "در حال بارگزاری..." : "بارگزاری تصاویر"}
            </button>
          )}
        </div>
      </div>
    </>
  );
};
